import { useState, useEffect } from 'react'
import { useAdvisors } from '../../hooks/useAdvisors'
import { useRoles } from '../../hooks/useRoles'
import { supabase } from '../../services/supabase'
import { Table } from '../../components/Common/Table'
import { Button } from '../../components/Common/Button'
import { UserForm } from './UserForm'
import { UserImport } from './UserImport'

export function UsersList({ sidebarCollapsed, isDarkMode = false }) {
  const [showForm, setShowForm] = useState(false)
  const [showImport, setShowImport] = useState(false)
  const [editingUser, setEditingUser] = useState(null)
  const [search, setSearch] = useState('')
  const [showInactive, setShowInactive] = useState(false)
  const [advisorRoles, setAdvisorRoles] = useState({}) // advisorId -> [roleName, ...]
  const { advisors, loading, toggleAdvisorActive, refetch } = useAdvisors()
  const { roles } = useRoles()

  const importEnabled = localStorage.getItem('sfp_enable_transaction_import') === 'true'

  // Load role assignments for display
  useEffect(() => {
    if (advisors.length === 0 || roles.length === 0) return
    supabase
      .from('advisor_roles')
      .select('advisor_id, role_id')
      .then(({ data }) => {
        if (!data) return
        const map = {}
        data.forEach(ar => {
          if (!map[ar.advisor_id]) map[ar.advisor_id] = []
          const role = roles.find(r => r.id === ar.role_id)
          if (role) map[ar.advisor_id].push(role.name)
        })
        setAdvisorRoles(map)
      })
  }, [advisors, roles])

  const handleImport = async (data) => {
    const { error } = await supabase
      .from('advisors')
      .upsert(data, { onConflict: 'email', ignoreDuplicates: false })
    if (error) throw error
    refetch()
  }

  const handleExport = async () => {
    const XLSX = await import('xlsx')
    const exportData = advisors.map(a => ({
      'Full Name': a.full_name || '',
      'Email': a.email || '',
      'Mobile': a.mobile || '',
      'Licence Type': a.licence_type || '',
      'Licence Number': a.licence_number || '',
      'Roles': (advisorRoles[a.id] || []).join(', '),
      'Active': a.active ? 'Yes' : 'No',
    }))
    const ws = XLSX.utils.json_to_sheet(exportData)
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Users')
    XLSX.writeFile(wb, 'Users.xlsx')
  }

  const columns = [
    { key: 'full_name', label: 'Full Name' },
    { key: 'email', label: 'Email' },
    { key: 'licence_type', label: 'Licence Type', render: (row) => row.licence_type || '-' },
    { key: 'licence_number', label: 'Licence No.', render: (row) => row.licence_number || '-' },
    {
      key: 'roles',
      label: 'Roles',
      render: (row) => {
        const names = advisorRoles[row.id]
        return names?.length ? (
          <div className="flex flex-wrap gap-1">
            {names.map(n => (
              <span key={n} className={`text-xs font-semibold px-2 py-0.5 rounded ${isDarkMode ? 'bg-slate-700 text-amber-400' : 'bg-orange-100 text-sfp-orange'}`}>
                {n}
              </span>
            ))}
          </div>
        ) : <span className={`text-xs ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`}>No role</span>
      }
    },
    {
      key: 'active',
      label: 'Active',
      render: (row) => (
        <button
          onClick={(e) => { e.stopPropagation(); toggleAdvisorActive(row.id, row.active) }}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${row.active ? 'bg-green-500' : 'bg-gray-300'}`}
          title={row.active ? 'Click to deactivate' : 'Click to activate'}
        >
          <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${row.active ? 'translate-x-6' : 'translate-x-1'}`} />
        </button>
      )
    },
    {
      key: 'actions',
      label: '',
      render: (row) => (
        <button
          onClick={(e) => { e.stopPropagation(); setEditingUser(row); setShowForm(true) }}
          className="text-sfp-orange hover:text-orange-700 text-xs font-semibold px-2 py-1 rounded hover:bg-orange-50 transition"
        >
          Edit
        </button>
      )
    },
  ]

  const filteredAdvisors = advisors.filter(a => {
    const matchesSearch = a.full_name?.toLowerCase().includes(search.toLowerCase()) ||
                         a.email?.toLowerCase().includes(search.toLowerCase())
    const matchesActive = showInactive || a.active
    return matchesSearch && matchesActive
  })

  return (
    <div className={`p-6 space-y-6 transition-colors duration-300 ${isDarkMode ? 'bg-slate-900' : 'bg-white'}`}>
      <div className="flex justify-between items-center gap-4 flex-wrap">
        <div className="flex gap-2 flex-wrap">
          <Button variant="primary" onClick={() => { setEditingUser(null); setShowForm(true) }}>
            + Add User
          </Button>
          {importEnabled && (
            <Button variant="secondary" onClick={() => setShowImport(true)}>
              ↑ Import Excel
            </Button>
          )}
          <Button variant="secondary" onClick={handleExport}>
            ↓ Export Excel
          </Button>
          <label className={`flex items-center gap-2 px-3 py-1.5 border rounded cursor-pointer text-sm transition-colors duration-300 ${isDarkMode ? 'border-slate-700 hover:bg-slate-800 text-slate-300' : 'border-gray-300 hover:bg-gray-50 text-gray-700'}`}>
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
              className="rounded cursor-pointer"
            />
            <span className="font-medium">Show Inactive</span>
          </label>
        </div>
        <input
          type="text"
          placeholder="Search by name or email..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="input-field max-w-xs"
        />
      </div>

      <div className={`overflow-hidden rounded border transition-colors duration-300 ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200 shadow-sm'}`}>
        <Table
          columns={columns}
          data={filteredAdvisors}
          loading={loading}
          onRowClick={(row) => { setEditingUser(row); setShowForm(true) }}
          isDarkMode={isDarkMode}
        />
      </div>

      {showForm && (
        <UserForm
          initialData={editingUser}
          onClose={() => { setShowForm(false); setEditingUser(null); refetch() }}
          sidebarCollapsed={sidebarCollapsed}
          isDarkMode={isDarkMode}
        />
      )}

      {showImport && (
        <UserImport
          onImport={handleImport}
          onClose={() => setShowImport(false)}
          sidebarCollapsed={sidebarCollapsed}
          isDarkMode={isDarkMode}
        />
      )}
    </div>
  )
}
